import { useMemo, useState } from 'react'
import { useScopeStore } from '../store'
import {
  PRESALES_ACTIONS,
  PRESALES_STEPS,
  DEPRECATED_ACTION_IDS,
} from '../types'
import type {
  PresalesActionItem,
  PresalesActionDef,
  PresalesStepDef,
} from '../types'
import { Card } from '@/components/ui/Card'
import { ProgressDonut, PROGRESS_STEP_COLORS } from './ProgressDonut'
import { computeStepStatuses } from '../utils/progressSteps'
import type { StepStatus } from '../utils/progressSteps'

export function ActionsTab() {
  const { scope, updateGeneralInfo } = useScopeStore()
  const disabled = scope.finalized
  const actions = useMemo(
    () => (scope.generalInfo.presalesActions || []).filter((a) => !DEPRECATED_ACTION_IDS.has(a.id)),
    [scope.generalInfo.presalesActions],
  )
  const scopeStatusDates = scope.generalInfo.scopeStatusDates || ({} as Record<string, string | null>)

  const getAction = (id: string): PresalesActionItem => {
    return actions.find((a) => a.id === id) || { id, completed: false, completedAt: null, notRequired: false, notes: '' }
  }

  const writeActions = (next: PresalesActionItem[]) => {
    updateGeneralInfo('presalesActions', next)
  }

  const updateAction = (id: string, updates: Partial<PresalesActionItem>) => {
    const existing = getAction(id)
    const updated = { ...existing, ...updates }
    const next = actions.some((a) => a.id === id)
      ? actions.map((a) => (a.id === id ? updated : a))
      : [...actions, updated]
    writeActions(next)
  }

  const toggleAction = (def: PresalesActionDef) => {
    const current = getAction(def.id)
    const completed = !current.completed
    const completedAt = completed ? new Date().toISOString().split('T')[0] : null

    // Mutex group — ticking one unticks the others in the same mutexGroup.
    if (def.mutexGroup && completed) {
      const peers = PRESALES_ACTIONS.filter((a) => a.mutexGroup === def.mutexGroup && a.id !== def.id).map((a) => a.id)
      const updated = actions.map((a) => {
        if (a.id === def.id) return { ...a, completed: true, completedAt, notRequired: false }
        if (peers.includes(a.id)) return { ...a, completed: false, completedAt: null }
        return a
      })
      if (!updated.some((a) => a.id === def.id)) {
        updated.push({ id: def.id, completed: true, completedAt, notRequired: false, notes: '' })
      }
      writeActions(updated)
      return
    }

    updateAction(def.id, { completed, completedAt, notRequired: false })
  }

  const toggleNotRequired = (id: string) => {
    const current = getAction(id)
    const notRequired = !current.notRequired
    updateAction(id, { notRequired, completed: false, completedAt: null })
  }

  const blueprintPercent = useScopeStore((s) => s.tabCompletions.blueprint ?? 0)
  const stepStatuses: StepStatus[] = useMemo(
    () => computeStepStatuses(actions, scopeStatusDates, blueprintPercent),
    [actions, scopeStatusDates, blueprintPercent],
  )

  const presalesDefs = PRESALES_ACTIONS.filter((a) => a.group === 'presales')
  const implDefs = PRESALES_ACTIONS.filter((a) => a.group === 'implementations')

  return (
    <div className="space-y-8">
      {/* ── Progress Donut ────────────────────────────────────────────── */}
      <ProgressPanel statuses={stepStatuses} />

      {/* ── Presales Actions list ────────────────────────────────────── */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">Presales Actions</h3>
          <span className="text-xs text-[var(--text-muted)]">Drives the progress visualisation above.</span>
        </div>
        <Card className="divide-y divide-[var(--border-color)]">
          {presalesDefs.map((def) => (
            <ActionRow
              key={def.id}
              def={def}
              action={getAction(def.id)}
              disabled={disabled}
              onToggle={() => toggleAction(def)}
              onToggleNotRequired={() => toggleNotRequired(def.id)}
              onNotesChange={(notes) => updateAction(def.id, { notes })}
            />
          ))}
        </Card>
      </div>

      {/* ── Implementations Actions list (separated) ─────────────────── */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">Implementations Actions</h3>
          <span className="text-xs text-[var(--text-muted)]">Handled by the Implementations team — tracked here but not part of presales progress.</span>
        </div>
        <Card className="divide-y divide-[var(--border-color)]">
          {implDefs.map((def) => (
            <ActionRow
              key={def.id}
              def={def}
              action={getAction(def.id)}
              disabled={disabled}
              onToggle={() => toggleAction(def)}
              onToggleNotRequired={() => toggleNotRequired(def.id)}
              onNotesChange={(notes) => updateAction(def.id, { notes })}
            />
          ))}
        </Card>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// ProgressPanel — big donut with legend + two-column step list alongside
// ─────────────────────────────────────────────────────────────────────────
function ProgressPanel({ statuses }: { statuses: StepStatus[] }) {
  const [hovered, setHovered] = useState<{ def: PresalesStepDef; status: StepStatus; index: number } | null>(null)

  return (
    <Card className="p-5">
      <div className="flex items-start gap-6">
        <div className="shrink-0">
          <ProgressDonut statuses={statuses} size={220} onHoverChange={setHovered} />
        </div>
        <div className="flex-1 min-w-0 space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Presales Progress</h3>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              {hovered ? hovered.def.description : 'Hover any slice for details. Each slice is one step of the presales flow.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--text-muted)]">
            <span>✓ Done ({statuses.filter((s) => s.state === 'done').length})</span>
            <span>○ Pending ({statuses.filter((s) => s.state === 'pending').length})</span>
            {statuses.some((s) => s.state === 'na') && (
              <span>∅ N/A ({statuses.filter((s) => s.state === 'na').length})</span>
            )}
            <span className="opacity-70 italic">Each slice has its own colour. Opacity shows progress.</span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
            {PRESALES_STEPS.map((def, idx) => {
              const s = statuses[idx]
              const stepColor = PROGRESS_STEP_COLORS[idx % PROGRESS_STEP_COLORS.length]
              const dotOpacity = s.state === 'done' ? 1 : s.state === 'na' ? 0.3 : 0.35
              return (
                <div
                  key={def.id}
                  className={`flex items-center gap-2 ${hovered?.index === idx ? 'font-semibold' : ''}`}
                >
                  <span
                    className="inline-block h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: stepColor, opacity: dotOpacity }}
                  />
                  <span className={s.state === 'pending' || s.state === 'na' ? 'text-[var(--text-muted)]' : 'text-[var(--text-secondary)]'}>
                    {idx + 1}. {def.label}
                    {s.state === 'done' && ' ✓'}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// ActionRow — single action checkbox with Not Required toggle + notes
// ─────────────────────────────────────────────────────────────────────────
interface ActionRowProps {
  def: PresalesActionDef
  action: PresalesActionItem
  disabled: boolean
  onToggle: () => void
  onToggleNotRequired: () => void
  onNotesChange: (notes: string) => void
}

function ActionRow({ def, action, disabled, onToggle, onToggleNotRequired, onNotesChange }: ActionRowProps) {
  const isNotRequired = action.notRequired
  return (
    <div className={`flex items-start gap-4 p-4 ${isNotRequired ? 'opacity-60' : ''}`}>
      <input
        type="checkbox"
        checked={action.completed}
        onChange={onToggle}
        disabled={disabled || isNotRequired}
        className="mt-1 h-4 w-4 rounded border-[var(--border-color)] text-[var(--button-primary-bg)] focus:ring-[var(--button-primary-bg)] cursor-pointer disabled:cursor-not-allowed"
      />
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-3">
          <span className={`text-sm font-medium ${isNotRequired ? 'text-[var(--text-muted)] line-through' : action.completed ? 'text-[var(--text-muted)] line-through' : 'text-[var(--text-primary)]'}`}>
            {def.label}
          </span>
          {def.mutexGroup && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-secondary)] text-[var(--text-muted)] uppercase tracking-wide" title="Mutually exclusive — ticking this will untick its alternative.">
              alt
            </span>
          )}
          {isNotRequired && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--bg-secondary)] text-[var(--text-muted)]">N/A</span>
          )}
          {action.completedAt && !isNotRequired && (
            <span className="text-xs text-[var(--text-muted)]">{action.completedAt}</span>
          )}
          <button
            type="button"
            onClick={onToggleNotRequired}
            disabled={disabled}
            className={`ml-auto text-xs px-2 py-0.5 rounded border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isNotRequired
                ? 'border-[var(--button-primary-bg)] text-[var(--button-primary-bg)] bg-[var(--button-primary-bg)]/10'
                : 'border-[var(--border-color)] text-[var(--text-muted)] hover:border-[var(--text-muted)]'
            }`}
          >
            Not Required
          </button>
        </div>
        {def.description && (
          <p className="text-xs text-[var(--text-muted)]">{def.description}</p>
        )}
        {!isNotRequired && (
          <input
            type="text"
            placeholder="Notes..."
            value={action.notes}
            onChange={(e) => onNotesChange(e.target.value)}
            disabled={disabled}
            className="w-full text-sm px-2 py-1 rounded bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-color)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[var(--button-primary-bg)] disabled:opacity-50"
          />
        )}
      </div>
    </div>
  )
}
